import { word } from "@/modules/core/utils/i18n"
import type { Language } from "@/modules/language/utils/languageUtils"
import type { UserProgramDetailsEntity } from "@/modules/programs/entities/userProgramDetailsEntity"
import type { ScoreFormikType } from "@/modules/scores/scoreForms/scoreFormikTypes"
import { getScoreDifficultyWording, ScoreDifficultyEnum, ScoreResultType, ScoreType } from "@/modules/scores/scoreTypes"
import { getDisplayedScore, getScoreResultTypeCaption } from "@/modules/scores/scoreUtils"
import type { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import type { HUUnit } from "@/modules/utils/unitType"

export type ScoreFormModel = {
  formTitle: string
  formSubtitle: string
  displayedScore: string
  displayIsScoreMediaRequired: boolean
  displayScoreTotal: boolean
  resultTypeCaption: string | null
  displayTotalMissingReps: boolean
  missingRepsCount: number
  difficultyOptions: { label: string; value: ScoreDifficultyEnum }[]
}

export function createScoreFormModel(
  program: UserProgramDetailsEntity,
  exercise: SessionBlockExerciseEntity,
  exerciseScoreFormikValues: ScoreFormikType,
  unit: HUUnit,
  language: Language,
): ScoreFormModel {
  return {
    formTitle:
      exercise.scoreSetConfigurations.length > 1
        ? word("session.score.form.multipleScoreSets.title")
        : word("session.score.form.singleScoreSet.title"),
    formSubtitle: program.scoreboardLocked
      ? exercise.scoreSetConfigurations.length > 1
        ? word("session.score.form.multipleScoreSets.scoreboardLocked.subtitle")
        : word("session.score.form.singleScoreSet.scoreboardLocked.subtitle")
      : exercise.scoreSetConfigurations.length > 1
        ? word("session.score.form.multipleScoreSets.scoreboardVisible.subtitle")
        : word("session.score.form.singleScoreSet.scoreboardVisible.subtitle"),
    /**
     * displayedScore
     * If one of the scoreSetCompletions has a time cap exceeded, we display the time cap exceeded label
     * If all scoreSetCompletions have the same scoreType, we display the score
     * If the valueDecimal is not null, we display the valueDecimal
     * If the valueDecimal is null, we display an empty string
     **/
    displayedScore: exerciseScoreFormikValues.scoreSetCompletions.some(
      (scoreSetCompletion) => scoreSetCompletion.scoreType === ScoreType.TIME && scoreSetCompletion.timeCapExceeded,
    ) // time cap exceeded
      ? word("session.score.form.timeCapExceeded.label")
      : !!exerciseScoreFormikValues.valueDecimal &&
          exerciseScoreFormikValues.scoreSetCompletions[0] &&
          exerciseScoreFormikValues.scoreSetCompletions.every(
            (scoreSetCompletion) =>
              scoreSetCompletion.scoreType === exerciseScoreFormikValues.scoreSetCompletions[0]?.scoreType,
          ) // all scoreSetCompletions have the same scoreType
        ? getDisplayedScore(
            exerciseScoreFormikValues.valueDecimal,
            exerciseScoreFormikValues.additionalValueDecimal,
            exerciseScoreFormikValues.scoreSetCompletions[0]?.scoreType ?? ScoreType.NO_SCORE,
            exerciseScoreFormikValues.timeCapExceeded ?? false,
            unit,
            language,
          )
        : exerciseScoreFormikValues.valueDecimal // valueDecimal is not null
          ? exerciseScoreFormikValues.valueDecimal.toString()
          : "",
    displayIsScoreMediaRequired: exercise.requiredMedia,
    displayScoreTotal:
      exercise.scoreSetConfigurations.length > 1 &&
      !!exercise.scoringOption &&
      exercise.scoringOption !== ScoreResultType.SET_N_FOR_SCOREBOARD,
    resultTypeCaption: exercise.scoringOption ? getScoreResultTypeCaption(exercise.scoringOption, true) : null,
    displayTotalMissingReps:
      exerciseScoreFormikValues.scoreSetCompletions.length > 1 &&
      exerciseScoreFormikValues.scoreSetCompletions.some(
        (scoreSetCompletion) => scoreSetCompletion.scoreType === ScoreType.TIME && scoreSetCompletion.timeCapExceeded,
      ),
    missingRepsCount: exerciseScoreFormikValues.scoreSetCompletions
      .filter(
        (scoreSetCompletion) => scoreSetCompletion.scoreType === ScoreType.TIME && scoreSetCompletion.timeCapExceeded,
      )
      .reduce((sum, scoreSetCompletion) => sum + (scoreSetCompletion.score ?? 0), 0),
    difficultyOptions: Object.values(ScoreDifficultyEnum).map((difficulty) => ({
      label: getScoreDifficultyWording(difficulty),
      value: difficulty,
    })),
  }
}
